import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { listAnnouncements, setAnnouncement, type Announcement } from '@/api/announcement';
import { CloseX, Chip, AllVendosRow, lockedMap, NeedsTag } from '@/components/Admin/FreePromoPanel';

/**
 * Dashboard → Announcement (Ray 2026-10-23): one line the owner types that
 * scrolls right to left at the top of the WiFi page ("Closed on Sunday",
 * "New rate: ₱5 = 1 hour"). Router-wide, so only main vendos are listed.
 * Same strip height as the free-time promo above it.
 */

export const ANNOUNCEMENT_MAX = 120;
const UNTIL_CHOICES: { label: string; days: number | null }[] = [
   { label: 'Today', days: 0 },
   { label: '3 days', days: 3 },
   { label: '1 week', days: 7 },
   { label: 'No end', days: null },
];

/** End of today (0) or N days from now; null = no end. */
export function untilFor(days: number | null, now = new Date()): string | null {
   if (days === null) return null;
   const d = new Date(now);
   if (days === 0) d.setHours(23, 59, 59, 0);
   else d.setTime(now.getTime() + days * 86_400_000);
   return d.toISOString();
}

/** The posted messages, one row per distinct text (it may be on several vendos). */
function groupByText(rows: Announcement[]) {
   const map = new Map<
      string,
      { text: string; until: string | null; bg: string; fg: string; ids: string[]; rows: Announcement[] }
   >();
   for (const r of rows) {
      const g = map.get(r.text) ?? { text: r.text, until: r.until, bg: r.bg ?? '', fg: r.fg ?? '', ids: [], rows: [] };
      g.ids.push(r.deviceId);
      g.rows.push(r);
      map.set(r.text, g);
   }
   return [...map.values()];
}

const AnnouncementPanel: React.FC = () => {
   const queryClient = useQueryClient();
   const { data: res } = useQuery({
      queryKey: ['announcements'],
      queryFn: listAnnouncements,
      // Quick while a router has yet to confirm it, so "Waiting" turns
      // "Showing" within seconds of the router's check-in.
      refetchInterval: (q) =>
         ((q.state.data as { announcements?: Announcement[] } | undefined)?.announcements ?? []).some(
            (r) => r.state === 'pending'
         )
            ? 10_000
            : 60_000,
   });
   const data = res?.announcements ?? [];
   const locked = lockedMap(res?.routers);
   const [openStates, setOpenStates] = useState<string | null>(null);
   const [edit, setEdit] = useState<{ text: string; ids: string[] | null; bg?: string; fg?: string } | null>(null);
   const [removing, setRemoving] = useState(false);
   const groups = groupByText(data);

   const remove = async (ids: string[]) => {
      if (removing) return;
      setRemoving(true);
      try {
         await setAnnouncement({ deviceIds: ids, text: '', until: null });
         toast.success('Announcement removed');
         await queryClient.invalidateQueries({ queryKey: ['announcements'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not remove it');
      } finally {
         setRemoving(false);
      }
   };

   return (
      <>
         {groups.length === 0 && (
            <Strip data-testid="announce-empty">
               <Icon aria-hidden>📢</Icon>
               <Txt>
                  <Title>Announcement</Title>
                  <Sub>Tell your customers something — closed days, new rates. It scrolls at the top of your WiFi page.</Sub>
               </Txt>
               <PostAct>
                  <Btn type="button" $primary onClick={() => setEdit({ text: '', ids: null })}>
                     + Post announcement
                  </Btn>
               </PostAct>
            </Strip>
         )}
         {groups.map((g) => (
            <Strip key={g.text} data-testid="announce-live">
               <Icon aria-hidden>📢</Icon>
               <Txt>
                  <Showing>📢 ANNOUNCEMENT</Showing>
                  <Ticker>
                     <Loop text={g.text} />
                  </Ticker>
                  <StateLine>
                     {STATES.map((st) => {
                        const n = g.rows.filter((r) => (r.state ?? 'live') === st.key).length;
                        return n ? (
                           <StateChip
                              key={st.key}
                              type="button"
                              $c={st.color}
                              aria-expanded={openStates === g.text + st.key}
                              onClick={() => setOpenStates(openStates === g.text + st.key ? null : g.text + st.key)}
                           >
                              <Dot $c={st.color} $pulse={st.key === 'pending'} />
                              {st.label} {n}
                           </StateChip>
                        ) : null;
                     })}
                  </StateLine>
                  {STATES.map((st) =>
                     openStates === g.text + st.key ? (
                        <StateNames key={st.key}>
                           {st.hint} {g.rows.filter((r) => (r.state ?? 'live') === st.key).map((r) => r.name).join(', ')}
                        </StateNames>
                     ) : null
                  )}
                  <Sub>
                     On {g.ids.length} vendo{g.ids.length === 1 ? '' : 's'} ·{' '}
                     {g.until
                        ? `until ${new Date(g.until).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                        : 'until you remove it'}
                  </Sub>
               </Txt>
               <Acts>
                  <Btn type="button" onClick={() => setEdit({ text: g.text, ids: g.ids, bg: g.bg, fg: g.fg })}>
                     Change
                  </Btn>
                  <Btn type="button" $danger disabled={removing} onClick={() => remove(g.ids)}>
                     Remove
                  </Btn>
               </Acts>
            </Strip>
         ))}
         {edit && (
            <PostModal
               initialText={edit.text}
               locked={locked}
               initialIds={edit.ids}
               initialBg={edit.bg ?? ''}
               initialFg={edit.fg ?? ''}
               onClose={() => setEdit(null)}
               onPosted={async () => {
                  setEdit(null);
                  await queryClient.invalidateQueries({ queryKey: ['announcements'] });
               }}
            />
         )}
      </>
   );
};

export default AnnouncementPanel;

/** Four copies sliding by half: the next copy enters as the last leaves. */
const Loop: React.FC<{ text: string }> = ({ text }) => (
   <span style={{ animationDuration: `${Math.max(10, Math.round(text.length * 0.44))}s` }}>
      {[0, 1, 2, 3].map((i) => (
         <em key={i} aria-hidden={i > 0 || undefined}>
            {text}
         </em>
      ))}
   </span>
);

/** Ready-made colour styles; '' = match the portal's own look. */
export const COLOUR_STYLES: { key: string; label: string; bg: string; fg: string }[] = [
   { key: 'portal', label: 'Portal', bg: '', fg: '' },
   { key: 'red', label: 'Alert', bg: '#dc2626', fg: '#ffffff' },
   { key: 'yellow', label: 'Notice', bg: '#facc15', fg: '#1f2937' },
   { key: 'green', label: 'Good', bg: '#16a34a', fg: '#ffffff' },
   { key: 'pink', label: 'Promo', bg: '#ec4899', fg: '#ffffff' },
   { key: 'black', label: 'Classic', bg: '#111827', fg: '#ffffff' },
];

const PostModal: React.FC<{
   initialText: string;
   initialIds: string[] | null;
   initialBg: string;
   initialFg: string;
   /** main vendo id → router update it still needs; those rows are locked */
   locked?: Record<string, string>;
   onClose: () => void;
   onPosted: () => void;
}> = ({ initialText, initialIds, initialBg, initialFg, locked = {}, onClose, onPosted }) => {
   const devices = useVendoDevicesStore((s) => s.devices);
   const fetched = useVendoDevicesStore((s) => s.fetched);
   const fetchDevices = useVendoDevicesStore((s) => s.fetch);
   useEffect(() => {
      if (!fetched) void fetchDevices();
   }, [fetched, fetchDevices]);
   // Router-wide: main vendos only (a sub-vendo shows its main's).
   const mains = useMemo(
      () =>
         devices
            .filter((d) => d.isCurrentOwner !== false && !d.parent)
            .sort((a, b) => a.name.localeCompare(b.name)),
      [devices]
   );
   const [text, setText] = useState(initialText);
   const [bg, setBg] = useState(initialBg);
   const [fg, setFg] = useState(initialFg);
   const preset = COLOUR_STYLES.find((c) => c.bg === bg && c.fg === fg);
   const [custom, setCustom] = useState(!preset);
   const [days, setDays] = useState<number | null>(null);
   const [picked, setPicked] = useState<Set<string> | null>(initialIds ? new Set(initialIds) : null);
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);
   const open = mains.filter((m) => !locked[m._id]);
   const ticked = picked ?? new Set(open.map((m) => m._id));
   const toggle = (id: string) =>
      setPicked(() => {
         const next = new Set(ticked);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });

   const submit = async () => {
      if (busy) return;
      const t = text.trim();
      if (!t) return setErr('Type the message, e.g. Closed on Sunday.');
      if (!ticked.size) return setErr('Tick at least one vendo.');
      setErr('');
      setBusy(true);
      try {
         await setAnnouncement({ deviceIds: [...ticked], text: t, until: untilFor(days), bg, fg });
         toast.success('Posted — each vendo shows it once its router checks in (about a minute)');
         onPosted();
      } catch (e: any) {
         setErr(e?.response?.data?.error || 'Could not post it');
      } finally {
         setBusy(false);
      }
   };

   // Never closes on an outside click — only Cancel (Ray's modal rule).
   return createPortal(
      <Overlay>
         <Dialog role="dialog" aria-label="Post an announcement">
            <Head>
               Post an announcement
               <CloseX type="button" aria-label="Close" onClick={onClose} disabled={busy}>
                  ✕
               </CloseX>
            </Head>
            <Body>
               <div>
                  <Label htmlFor="an-text">Message</Label>
                  <Input
                     id="an-text"
                     type="text"
                     maxLength={ANNOUNCEMENT_MAX}
                     placeholder="e.g. Closed on Sunday — open again Monday 7 AM"
                     value={text}
                     onChange={(e) => setText(e.target.value)}
                  />
                  <Count>
                     {text.length}/{ANNOUNCEMENT_MAX}
                  </Count>
               </div>
               <div>
                  <Label>Colours</Label>
                  <Swatches>
                     {COLOUR_STYLES.map((c) => (
                        <Swatch
                           key={c.key}
                           type="button"
                           $on={!custom && bg === c.bg && fg === c.fg}
                           aria-label={`${c.label} colours`}
                           onClick={() => {
                              setCustom(false);
                              setBg(c.bg);
                              setFg(c.fg);
                           }}
                        >
                           <SwatchPill style={{ background: c.bg || PORTAL_BG, color: c.fg || '#fff' }}>Aa</SwatchPill>
                           {c.label}
                        </Swatch>
                     ))}
                     <Swatch
                        type="button"
                        $on={custom}
                        aria-label="Custom colours"
                        onClick={() => {
                           setCustom(true);
                           if (!bg) setBg('#0ea5e9');
                           if (!fg) setFg('#ffffff');
                        }}
                     >
                        <SwatchPill style={{ background: 'conic-gradient(#f43f5e,#f59e0b,#22c55e,#3b82f6,#a855f7,#f43f5e)', color: '#fff' }}>
                           +
                        </SwatchPill>
                        Custom
                     </Swatch>
                  </Swatches>
                  {custom && (
                     <CustomRow>
                        <ColourField>
                           <input type="color" aria-label="Background colour" value={bg || '#0ea5e9'} onChange={(e) => setBg(e.target.value)} />
                           Background
                        </ColourField>
                        <ColourField>
                           <input type="color" aria-label="Text colour" value={fg || '#ffffff'} onChange={(e) => setFg(e.target.value)} />
                           Text
                        </ColourField>
                     </CustomRow>
                  )}
               </div>
               {text.trim() && (
                  <PreviewBar aria-label="How it looks" style={{ background: bg || PORTAL_BG, color: fg || '#fff' }}>
                     <Icon aria-hidden $small>
                        📢
                     </Icon>
                     <Ticker $light>
                        <Loop text={text.trim()} />
                     </Ticker>
                  </PreviewBar>
               )}
               <div>
                  <Label>Show until</Label>
                  <Grid>
                     {UNTIL_CHOICES.map((u) => (
                        <Chip key={u.label} type="button" $on={days === u.days} onClick={() => setDays(u.days)}>
                           {u.label}
                        </Chip>
                     ))}
                  </Grid>
               </div>
               <div>
                  <Label>Vendos</Label>
                  <List>
                     {open.length > 1 && (
                        <AllVendosRow
                           total={open.length}
                           ticked={open.filter((m) => ticked.has(m._id)).length}
                           onChange={(all) => setPicked(new Set(all ? open.map((m) => m._id) : []))}
                        />
                     )}
                     {mains.map((m) => (
                        <Row key={m._id} $locked={!!locked[m._id]} data-locked={locked[m._id] || undefined}>
                           <input
                              type="checkbox"
                              disabled={!!locked[m._id]}
                              checked={!locked[m._id] && ticked.has(m._id)}
                              onChange={() => toggle(m._id)}
                           />
                           {m.name}
                           {locked[m._id] && <NeedsTag>needs router update {locked[m._id]}</NeedsTag>}
                        </Row>
                     ))}
                     {!mains.length && <Sub>Loading your vendos…</Sub>}
                     {mains.length > 0 && !open.length && (
                        <Sub data-testid="all-locked" style={{ whiteSpace: 'normal' }}>
                           All your routers need router update {[...new Set(Object.values(locked))].join(' / ')} first.
                           Each one can show announcements as soon as it updates.
                        </Sub>
                     )}
                  </List>
               </div>
               {err && <ErrText role="alert">{err}</ErrText>}
            </Body>
            <Foot>
               <Btn type="button" $ghost onClick={onClose} disabled={busy}>
                  Cancel
               </Btn>
               <Btn type="button" $primary onClick={submit} disabled={busy}>
                  {busy ? 'Posting…' : 'Post'}
               </Btn>
            </Foot>
         </Dialog>
      </Overlay>,
      document.body
   );
};

/* ── styles ─────────────────────────────────────────────────────────── */

const STATES: { key: 'live' | 'pending' | 'offline' | 'update'; label: string; hint: string; color: string }[] = [
   { key: 'live', label: 'Showing', hint: 'Customers see it on:', color: '#16a34a' },
   { key: 'pending', label: 'Waiting for router', hint: 'Sent, the router confirms on its next check-in:', color: '#f59e0b' },
   { key: 'offline', label: 'Router offline', hint: 'Shows when these routers are back online:', color: '#94a3b8' },
   { key: 'update', label: 'Needs router update', hint: 'Shows after these routers get the new firmware:', color: '#e11d48' },
];

const pulseDot = keyframes`
   50% { opacity: 0.3; }
`;
const StateLine = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin: 3px 0 2px;
`;
const StateChip = styled.button<{ $c: string }>`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   border: 1px solid ${({ $c }) => `${$c}55`};
   background: ${({ $c }) => `${$c}14`};
   color: ${({ $c }) => $c};
   border-radius: 999px;
   padding: 2px 9px;
   font-size: 11.5px;
   font-weight: 700;
   cursor: pointer;
`;
const Dot = styled.i<{ $c: string; $pulse?: boolean }>`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   background: ${({ $c }) => $c};
   animation: ${({ $pulse }) => ($pulse ? pulseDot : 'none')} 1.2s infinite;
`;
const StateNames = styled.div`
   font-size: 12px;
   color: var(--app-text);
   background: ${colors.GRAY14};
   border-radius: 8px;
   padding: 6px 9px;
   margin: 2px 0 4px;
`;

const scroll = keyframes`
   from { transform: translateX(0); }
   to { transform: translateX(-50%); }
`;

const Strip = styled.div`
   border-radius: 14px;
   padding: 9px 14px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   display: grid;
   grid-template-columns: auto minmax(0, 1fr) auto;
   gap: 12px;
   align-items: center;
   margin-bottom: 16px;
   @media (max-width: 720px) {
      grid-template-columns: auto minmax(0, 1fr);
   }
`;
const Icon = styled.div<{ $small?: boolean }>`
   width: ${({ $small }) => ($small ? '20px' : '42px')};
   height: ${({ $small }) => ($small ? '20px' : '42px')};
   border-radius: 11px;
   display: grid;
   place-items: center;
   font-size: ${({ $small }) => ($small ? '15px' : '22px')};
   background: ${({ $small }) => ($small ? 'transparent' : 'var(--app-brand-tint)')};
   flex-shrink: 0;
`;
const Txt = styled.div`
   min-width: 0;
`;
const Showing = styled.span`
   display: inline-block;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   color: ${colors.PRIMARY};
   background: var(--app-brand-tint);
   padding: 2px 8px;
   border-radius: 999px;
   margin-bottom: 3px;
`;
const Title = styled.div`
   font-size: 16px;
   font-weight: 800;
   color: var(--app-text);
`;
const Sub = styled.div`
   font-size: 12.5px;
   color: ${colors.GRAY3};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   @media (max-width: 720px) {
      white-space: normal; /* phones: wrap, never cut the sentence */
   }
`;
/* The one button of the empty strip: own column on desktop; on phones a full
 * row under icon + text (as a bare grid child it widened the icon column and
 * shoved the text right — Ray 2026-10-25). */
const PostAct = styled.div`
   @media (max-width: 720px) {
      grid-column: 1 / -1;
      button {
         width: 100%;
      }
   }
`;
const Ticker = styled.div<{ $light?: boolean }>`
   overflow: hidden;
   white-space: nowrap;
   font-size: ${({ $light }) => ($light ? '13.5px' : '15px')};
   font-weight: 700;
   color: ${({ $light }) => ($light ? 'inherit' : 'var(--app-text)')};
   flex: 1;
   min-width: 0;
   > span {
      display: inline-flex;
      animation-name: ${scroll};
      animation-timing-function: linear;
      animation-iteration-count: infinite;
   }
   em {
      font-style: normal;
      padding-right: 48px;
   }
   @media (prefers-reduced-motion: reduce) {
      > span {
         animation: none;
      }
   }
`;
// The portal's default bar colour (Gaming look) for "match the portal".
const PORTAL_BG = '#6c5ce7';
const Swatches = styled.div`
   display: grid;
   grid-template-columns: repeat(7, minmax(0, 1fr));
   gap: 6px;
   @media (max-width: 460px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
   }
`;
const Swatch = styled.button<{ $on: boolean }>`
   display: grid;
   justify-items: center;
   gap: 4px;
   padding: 6px 2px;
   border-radius: 10px;
   border: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   background: ${({ $on }) => ($on ? 'var(--app-brand-tint)' : 'transparent')};
   font-size: 11px;
   font-weight: 700;
   color: var(--app-text);
   cursor: pointer;
`;
const SwatchPill = styled.span`
   width: 38px;
   height: 24px;
   border-radius: 999px;
   display: grid;
   place-items: center;
   font-size: 12px;
   font-weight: 800;
   box-shadow: 0 1px 3px rgb(0 0 0 / 0.2);
`;
const CustomRow = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 8px;
   margin-top: 8px;
`;
const ColourField = styled.label`
   display: flex;
   align-items: center;
   gap: 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   padding: 6px 10px;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   input {
      width: 30px;
      height: 24px;
      border: none;
      padding: 0;
      background: none;
      cursor: pointer;
   }
`;
const PreviewBar = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 11px 18px;
   border-radius: 999px;
   overflow: hidden;
   box-shadow: 0 4px 14px rgb(0 0 0 / 0.15);
   /* the text fades in / out at the edges instead of being cut */
   > div:last-child {
      mask-image: linear-gradient(90deg, transparent, #000 14px, #000 calc(100% - 14px), transparent);
      -webkit-mask-image: linear-gradient(90deg, transparent, #000 14px, #000 calc(100% - 14px), transparent);
   }
`;
const Acts = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 6px;
   width: 200px;
   @media (max-width: 720px) {
      grid-column: 1 / -1;
      width: auto;
   }
`;
const Btn = styled.button<{ $primary?: boolean; $danger?: boolean; $ghost?: boolean }>`
   border-radius: 9px;
   padding: 8px 12px;
   font-size: 13px;
   font-weight: 700;
   white-space: nowrap;
   cursor: pointer;
   border: 1px solid
      ${({ $primary, $danger }) => ($primary ? colors.PRIMARY : $danger ? 'rgb(225 29 72 / 0.3)' : colors.GRAY12)};
   background: ${({ $primary }) => ($primary ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $primary, $danger }) => ($primary ? '#fff' : $danger ? '#e11d48' : 'var(--app-text)')};
   ${({ $ghost }) => ($ghost ? `background: transparent; border-color: transparent; color: ${colors.GRAY3};` : '')}
   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;
const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgb(0 0 0 / 0.45);
   display: grid;
   place-items: center;
   padding: 16px;
   z-index: 1000;
   @media (max-width: 520px) {
      place-items: end center;
      padding: 0;
   }
`;
const Dialog = styled.div`
   background: ${colors.WHITE};
   color: var(--app-text);
   border-radius: 16px;
   width: 100%;
   max-width: 460px;
   max-height: 90vh;
   display: flex;
   flex-direction: column;
   @media (max-width: 520px) {
      border-radius: 16px 16px 0 0;
   }
`;
const Head = styled.h3`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin: 0;
   padding: 20px 24px 14px;
   font-size: 16px;
   font-weight: 700;
   border-bottom: 1px solid ${colors.GRAY13};
`;
const Body = styled.div`
   padding: 20px 24px;
   display: grid;
   gap: 18px;
   overflow: auto;
`;
const Foot = styled.div`
   padding: 16px 24px 20px;
   border-top: 1px solid ${colors.GRAY13};
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 8px;
`;
const Label = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
   margin-bottom: 6px;
   text-transform: uppercase;
   letter-spacing: 0.03em;
`;
const Input = styled.input`
   width: 100%;
   padding: 9px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   font-size: 14px;
   font-family: inherit;
`;
const Count = styled.div`
   text-align: right;
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-top: 3px;
`;
const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, minmax(0, 1fr));
   gap: 6px;
`;
const List = styled.div`
   max-height: 240px;
   overflow: auto;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   padding: 4px 14px 6px;
   display: grid;
`;
const Row = styled.label<{ $locked?: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 14px;
   padding: 8px 0;
   ${({ $locked }) => ($locked ? 'opacity: 0.55; cursor: not-allowed;' : '')}
`;
const ErrText = styled.div`
   color: #dc2626;
   font-size: 13px;
`;
